import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import ErrorFallbackScreen from './ErrorFallbackScreen';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const reloadPage = () => window.location.reload();

/**
 * Catches React rendering errors (e.g. browser auto-translate mutating
 * the DOM) and shows a recovery screen instead of a white crash page.
 * Reports the error to Sentry automatically.
 *
 * NB: route render errors never reach this boundary — React Router
 * handles them first; those are covered by RouteErrorFallback (the
 * router's errorElement), which shares the same fallback screen.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return <ErrorFallbackScreen onReload={reloadPage} />;
    }

    return children;
  }
}

export default ErrorBoundary;
